import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { FaEdit, FaTrash, FaTimes, FaCloudUploadAlt, FaRegCalendarAlt } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import { apiFetch, API_BASE } from "./utils/api";
import SectionVisibility from "./utils/Dolluzcorp_SectionVisibility";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import "./Dolluzcorp_Updates_config.css";

// react-select renders its menu inline by default, so any ancestor with
// overflow:hidden (our form cards) clips it. Portal the menu to <body>.
const SELECT_PORTAL = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

const AUDIENCE_TYPES = [
    { value: "general", label: "General - Everyone (public)" },
    { value: "all_employees", label: "All Employees (logged-in staff)" },
    { value: "department", label: "Department" },
    { value: "job_position", label: "Job Position" },
    { value: "location", label: "Location" },
    { value: "access_level", label: "Access Level" },
    { value: "employee", label: "Employee" },
];
const SCOPED = ["department", "job_position", "location", "access_level", "employee"];
const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
const ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

const Dolluzcorp_Updates_config = ({ navSize }) => {
    const navigate = useNavigate();
    const [updates, setUpdates] = useState([]);

    const [title, setTitle] = useState("");
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [audienceType, setAudienceType] = useState("general");
    const [audienceValue, setAudienceValue] = useState([]);
    const [files, setFiles] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [deletedImageIds, setDeletedImageIds] = useState([]);
    const [imagesMaxHeight, setImagesMaxHeight] = useState(null);   // rendered px height for this update's images, null = natural size
    const [displayFrom, setDisplayFrom] = useState("");
    const [displayTo, setDisplayTo] = useState("");
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [previewMode, setPreviewMode] = useState("card");   // "card" | "full"

    const [departments, setDepartments] = useState([]);
    const [jobPositions, setJobPositions] = useState([]);
    const [employees, setEmployees] = useState([]);

    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["clean"],
            ],
        },
    });

    useEffect(() => { verifySession(); fetchUpdates(); fetchDepartments(); fetchJobPositions(); fetchEmployees(); }, []);
    useEffect(() => {
        if (!quill) return;
        const handler = () => setDescription(quill.root.innerHTML);
        quill.on("text-change", handler);
        return () => quill.off("text-change", handler);
    }, [quill]);

    // object URLs for newly-picked files (revoked when they change)
    const filePreviews = useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
    useEffect(() => () => filePreviews.forEach(URL.revokeObjectURL), [filePreviews]);

    const verifySession = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") navigate("/login");
        } catch (err) { console.error("session check failed:", err); }
    };

    const fetchUpdates = async () => { try { const r = await apiFetch("/api/Dolluzcorp/updates/list"); const d = await r.json(); if (d.success) setUpdates(d.data); } catch (e) { console.error(e); } };
    const fetchDepartments = async () => { try { const r = await apiFetch("/api/Dolluzcorp/departments/list"); const d = await r.json(); if (d.success) setDepartments(d.data); } catch { /* ignore */ } };
    const fetchJobPositions = async () => { try { const r = await apiFetch("/api/Employee_configuration/get-job_position_config"); setJobPositions(await r.json()); } catch { /* ignore */ } };
    const fetchEmployees = async () => { try { const r = await apiFetch("/api/employee/directory"); setEmployees(await r.json()); } catch { /* ignore */ } };

    const audienceOptionsFor = (type) => {
        switch (type) {
            case "department": return departments.map(d => ({ value: String(d.department_id), label: d.department_name }));
            case "job_position": return jobPositions.map(j => ({ value: String(j.job_id), label: j.job_name }));
            case "location": return LOCATIONS.map(l => ({ value: l, label: l }));
            case "access_level": return ACCESS_LEVELS.map(l => ({ value: l, label: l }));
            case "employee": return employees.map(e => ({ value: String(e.emp_id), label: `${e.emp_first_name} ${e.emp_last_name}` }));
            default: return [];
        }
    };

    const audienceBadge = (aType, aVal) => {
        if (aType === "general" || !aType) return "Everyone";
        if (aType === "all_employees") return "All Employees";
        const count = (aVal && aVal !== "ALL") ? aVal.split(",").filter(Boolean).length : 0;
        const label = { department: "Department", job_position: "Job Position", location: "Location", access_level: "Access Level", employee: "Employee" }[aType] || aType;
        return `${label}${count ? ` (${count})` : ""}`;
    };

    const removeExistingImage = (img) => {
        setDeletedImageIds(prev => [...prev, img.image_id]);
        setExistingImages(prev => prev.filter(i => i.image_id !== img.image_id));
    };

    const isScoped = SCOPED.includes(audienceType);
    const previewImages = [...existingImages.map(i => `${API_BASE}/${i.image_path}`), ...filePreviews];
    const todayStr = new Date().toLocaleDateString("en-CA");   // local YYYY-MM-DD for the date min

    const validate = () => {
        if (!title.trim() || !subject.trim() || !description || description === "<p><br></p>") return "Title, Subject and Description are required.";
        if (isScoped && audienceValue.length === 0) return "Pick at least one value for the selected audience.";
        if (displayFrom && displayTo && displayFrom > displayTo) return "Display From cannot be after Display To.";
        return null;
    };

    const submitUpdate = async () => {
        if (saving) return;
        const err = validate();
        if (err) { dnAlert.warning("Check the form", err); return; }

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("title", title.trim());
            fd.append("subject", subject.trim());
            fd.append("description", description);
            fd.append("audience_type", audienceType);
            fd.append("audience_value", isScoped ? audienceValue.join(",") : "");
            fd.append("display_from", displayFrom);
            fd.append("display_to", displayTo);
            fd.append("deletedImages", JSON.stringify(deletedImageIds));
            fd.append("images_max_height", imagesMaxHeight || "");
            files.forEach(f => fd.append("images", f));

            let url = "/api/Dolluzcorp/updates/create";
            if (editId) { fd.append("update_id", editId); url = "/api/Dolluzcorp/updates/update"; }

            const res = await apiFetch(url, { method: "POST", body: fd });
            const data = await res.json();
            if (!data.success) { if (data.devError) console.error("updates save:", data.devError); dnAlert.error("Couldn't save", data.message || "Something went wrong. Please try again."); return; }
            dnAlert.success("Success", editId ? "Announcement updated" : "Announcement published");
            resetForm();
            fetchUpdates();
        } catch (e) { dnAlert.error("Error", "Something went wrong"); }
        finally { setSaving(false); }
    };

    const startEdit = (u) => {
        setEditId(u.update_id);
        setTitle(u.title || "");
        setSubject(u.subject || "");
        setDescription(u.description || "");
        if (quill) quill.root.innerHTML = u.description || "";
        setAudienceType(u.audience_type || "general");
        setAudienceValue((u.audience_value && u.audience_value !== "ALL") ? u.audience_value.split(",").filter(Boolean) : []);
        setDisplayFrom(u.display_from ? u.display_from.slice(0, 10) : "");
        setDisplayTo(u.display_to ? u.display_to.slice(0, 10) : "");
        setFiles([]);
        setDeletedImageIds([]);
        setImagesMaxHeight(u.images_max_height || null);
        setExistingImages(u.images ? u.images.split(",").map(item => { const [id, path] = item.split("::"); return { image_id: Number(id), image_path: path }; }) : []);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const resetForm = () => {
        setEditId(null); setTitle(""); setSubject(""); setDescription("");
        setAudienceType("general"); setAudienceValue([]);
        setFiles([]); setExistingImages([]); setDeletedImageIds([]);
        setImagesMaxHeight(null);
        setDisplayFrom(""); setDisplayTo("");
        if (quill) quill.setText("");
    };

    const deleteUpdate = async (update_id) => {
        const ok = await dnAlert.confirm({ title: "Delete update?", message: "This action cannot be undone.", danger: true, confirmLabel: "Yes, delete" });
        if (!ok) return;
        const res = await apiFetch("/api/Dolluzcorp/updates/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ update_id }) });
        const data = await res.json();
        if (!data.success) { if (data.devError) console.error("updates delete:", data.devError); dnAlert.error("Couldn't delete", data.message || "Something went wrong. Please try again."); return; }
        dnAlert.success("Deleted", "Update removed");
        if (editId === update_id) resetForm();
        fetchUpdates();
    };

    const onPickFiles = (e) => {
        const selected = Array.from(e.target.files);
        if (existingImages.length + files.length + selected.length > 3) { dnAlert.warning("Limit", "Max 3 images allowed"); e.target.value = ""; return; }
        setFiles(prev => [...prev, ...selected]);
        e.target.value = "";
    };

    return (
        <div className="updates-container">
            <div className={`updates-dashboard ${navSize}`}>
                <div className="upd-header-wrapper">
                    <h2>
                        <img src={logo_eagle} alt="dAdmin Logo" className="upd-header-logo" />
                        Updates &amp; Announcements
                    </h2>
                    <p className="upd-subtitle">
                        Publish updates and announcements to the Inside D portal. Choose who sees each one, add rich text
                        and images, set a display window, and preview the portal card live as you write.
                    </p>
                </div>

                <div className="updates-dashboard-content">
                    <SectionVisibility section="updates" label="Updates & Announcements" />

                    <div className="upd-grid">
                        {/* ── LEFT: compose ── */}
                        <div className="upd-compose">
                            <div className="upd-card">
                                <div className="upd-card-head">
                                    <h4>{editId ? "Edit Announcement" : "New Announcement"}</h4>
                                    {editId && <span className="upd-edit-badge">EDIT</span>}
                                </div>

                                <div className="upd-form">
                                    <label className="upd-label">Who sees this?<span className="upd-req"> *</span></label>
                                    <Select
                                        classNamePrefix="upd-select" {...SELECT_PORTAL}
                                        options={AUDIENCE_TYPES}
                                        value={AUDIENCE_TYPES.find(a => a.value === audienceType)}
                                        onChange={(sel) => { setAudienceType(sel.value); setAudienceValue([]); }}
                                    />
                                    {isScoped && (
                                        <div className="upd-sub">
                                            <label className="upd-label">Select {audienceType.replace("_", " ")}</label>
                                            <Select
                                                key={audienceType}
                                                isMulti
                                                {...checkboxMultiProps}
                                                classNamePrefix="upd-select" {...SELECT_PORTAL}
                                                options={audienceOptionsFor(audienceType)}
                                                value={audienceOptionsFor(audienceType).filter(o => audienceValue.includes(String(o.value)))}
                                                onChange={(sel) => setAudienceValue((sel || []).map(s => String(s.value)))}
                                            />
                                        </div>
                                    )}

                                    <label className="upd-label">Images <span className="upd-hint">(optional, up to 3)</span></label>
                                    <div className="upd-file-box">
                                        <input type="file" id="updFileUpload" multiple accept="image/*" onChange={onPickFiles} />
                                        <label htmlFor="updFileUpload"><FaCloudUploadAlt /> {files.length ? `${files.length} new image(s)` : "Upload images"}</label>
                                    </div>
                                    <div className="upd-img-grid">
                                        {existingImages.map(img => (
                                            <div key={img.image_id} className="upd-img-box">
                                                <img src={`${API_BASE}/${img.image_path}`} alt="" />
                                                <span className="upd-img-remove" onClick={() => removeExistingImage(img)}><FaTimes /></span>
                                            </div>
                                        ))}
                                        {files.map((f, i) => (
                                            <div key={i} className="upd-img-box">
                                                <img src={filePreviews[i]} alt="" />
                                                <span className="upd-img-remove" onClick={() => setFiles(files.filter((_, idx) => idx !== i))}><FaTimes /></span>
                                            </div>
                                        ))}
                                    </div>
                                    {(existingImages.length > 0 || files.length > 0) && (
                                        <div className="upd-imgsize">
                                            <span className="upd-imgsize-label">
                                                Image height: <strong>{imagesMaxHeight ? `${imagesMaxHeight}px` : "Original size"}</strong>
                                            </span>
                                            <input
                                                type="range" min="80" max="800" step="10"
                                                value={imagesMaxHeight || 400}
                                                onChange={(e) => {
                                                    setImagesMaxHeight(Number(e.target.value));
                                                    // Images only appear in the full-page preview - the card is a
                                                    // teaser with no image - so without this the slider looks dead.
                                                    setPreviewMode("full");
                                                }}
                                            />
                                            {imagesMaxHeight && (
                                                <button type="button" className="upd-imgsize-reset" onClick={() => setImagesMaxHeight(null)}>Reset</button>
                                            )}
                                        </div>
                                    )}
                                    {(existingImages.length > 0 || files.length > 0) && (
                                        <p className="upd-hint upd-imgsize-hint">
                                            Sets the height of this update’s images on the portal (width scales with it).
                                            Shown in the <strong>Full page</strong> preview - the card is a teaser and has no image.
                                        </p>
                                    )}

                                    <label className="upd-label">Title<span className="upd-req"> *</span></label>
                                    <input className="upd-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Announcement title" maxLength={150} />

                                    <label className="upd-label">Subject<span className="upd-req"> *</span></label>
                                    <input className="upd-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Short subject line" maxLength={150} />

                                    <label className="upd-label">Description<span className="upd-req"> *</span></label>
                                    <div ref={quillRef} className="upd-editor" />

                                    <div className="upd-dates">
                                        <div>
                                            <label className="upd-label">Display From</label>
                                            <input type="date" className="upd-input" value={displayFrom} min={todayStr}
                                                onClick={(e) => e.target.showPicker?.()}
                                                onChange={e => { setDisplayFrom(e.target.value); if (displayTo && e.target.value > displayTo) setDisplayTo(""); }} />
                                        </div>
                                        <div>
                                            <label className="upd-label">Display To</label>
                                            <input type="date" className="upd-input" value={displayTo} min={displayFrom || todayStr}
                                                onClick={(e) => e.target.showPicker?.()} onChange={e => setDisplayTo(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="upd-actions">
                                        {editId && <button className="upd-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>}
                                        <button className="upd-btn-save" onClick={submitUpdate} disabled={saving}>
                                            {saving ? "Saving…" : editId ? "Update Announcement" : "Publish Announcement"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── RIGHT: live portal preview (card teaser ↔ full page) ── */}
                        <div className="upd-preview-col">
                            <div className="upd-preview-label">
                                <span className="upd-live" /> Portal preview
                                <div className="upd-preview-toggle">
                                    <button type="button" className={previewMode === "card" ? "active" : ""} onClick={() => setPreviewMode("card")}>Card</button>
                                    <button type="button" className={previewMode === "full" ? "active" : ""} onClick={() => setPreviewMode("full")}>Full page</button>
                                </div>
                            </div>

                            {previewMode === "card" ? (
                                <>
                                    <p className="upd-preview-note">How it looks in the Updates list on the portal.</p>
                                    <div className="upd-teaser">
                                        <div className="upd-teaser-accent" />
                                        <div className="upd-teaser-body">
                                            <div className="upd-teaser-top">
                                                <span className="upd-badge upd-badge-kind">Update</span>
                                                <span className="upd-teaser-date">{displayFrom ? fmtDate(displayFrom) : fmtDate(new Date())}</span>
                                            </div>
                                            <div className="upd-teaser-title">{title.trim() || "Announcement title"}</div>
                                            <div className="upd-teaser-sub">{subject.trim() || "Subject line"}</div>
                                            <span className="upd-teaser-more">Read more →</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="upd-preview-note">How it looks after clicking “Read more”.</p>
                                    <div className="upd-portal-card">
                                        {previewImages.length > 0 && (
                                            <div className="upd-portal-media">
                                                {/* maxHeight:"none" is essential - .upd-portal-media img carries a
                                                    240px max-height for the default look, and max-height is a
                                                    SEPARATE property from height, so it silently clamped the
                                                    inline height and the slider looked dead. */}
                                                <img
                                                    src={previewImages[0]}
                                                    alt=""
                                                    style={imagesMaxHeight
                                                        ? { height: `${imagesMaxHeight}px`, maxHeight: "none", width: "auto", maxWidth: "100%", objectFit: "contain" }
                                                        : undefined}
                                                />
                                                {previewImages.length > 1 && <span className="upd-portal-more">+{previewImages.length - 1}</span>}
                                            </div>
                                        )}
                                        <div className="upd-portal-body">
                                            <div className="upd-portal-badges">
                                                <span className="upd-badge upd-badge-aud">{audienceBadge(audienceType, audienceValue.join(","))}</span>
                                                {(displayFrom || displayTo) && (
                                                    <span className="upd-badge upd-badge-date"><FaRegCalendarAlt size={9} /> {fmtDate(displayFrom)} – {fmtDate(displayTo)}</span>
                                                )}
                                            </div>
                                            <div className="upd-portal-title">{title.trim() || "Announcement title"}</div>
                                            <div className="upd-portal-subject">{subject.trim() || "Subject line"}</div>
                                            <div className="upd-portal-desc" dangerouslySetInnerHTML={{ __html: (description && description !== "<p><br></p>") ? description : "<p style='color:#94a3b8'>Your description will appear here…</p>" }} />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── Published announcements ── */}
                    <div className="upd-published">
                        <div className="upd-published-head">Published Announcements <span className="upd-count">{updates.length}</span></div>
                        {updates.length === 0 ? (
                            <div className="upd-empty">
                                <div className="upd-empty-icon">📣</div>
                                <p>No announcements yet - create one above.</p>
                            </div>
                        ) : (
                            <div className="upd-list-grid">
                                {updates.map(u => {
                                    const imgs = u.images ? u.images.split(",").map(it => it.includes("::") ? it.split("::")[1] : it) : [];
                                    return (
                                        <div key={u.update_id} className="upd-item">
                                            {imgs.length > 0 && (
                                                <div className="upd-item-media">
                                                    <img src={`${API_BASE}/${imgs[0]}`} alt="" />
                                                    {imgs.length > 1 && <span className="upd-portal-more">+{imgs.length - 1}</span>}
                                                </div>
                                            )}
                                            {/* teaser card - exactly what the portal list shows */}
                                            <div className="upd-item-body">
                                                <div className="upd-portal-badges">
                                                    <span className="upd-badge upd-badge-aud">{audienceBadge(u.audience_type, u.audience_value)}</span>
                                                    {(u.display_from || u.display_to) && (
                                                        <span className="upd-badge upd-badge-date"><FaRegCalendarAlt size={9} /> {fmtDate(u.display_from)} – {fmtDate(u.display_to)}</span>
                                                    )}
                                                </div>
                                                <div className="upd-portal-title">{u.title}</div>
                                                <div className="upd-teaser-sub">{u.subject}</div>
                                                <span className="upd-teaser-more">Read more →</span>
                                                <div className="upd-item-foot">
                                                    <span className="upd-item-time">Updated {new Date(u.updated_time).toLocaleString()}</span>
                                                    <div className="upd-item-actions">
                                                        <button className="upd-icon-btn" onClick={() => startEdit(u)} title="Edit"><FaEdit /></button>
                                                        <button className="upd-icon-btn upd-icon-del" onClick={() => deleteUpdate(u.update_id)} title="Delete"><FaTrash /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dolluzcorp_Updates_config;
